/**
 * Catalog export/import utilities for the Character Library.
 * Handles exporting all characters as a library JSON and importing single or multiple characters.
 */
import type { CharacterDraft } from '../stores/types/characterDraft';
import type { RuntimeState, CharacterExportData } from './characterExport';
import type { StoredCharacter } from './characterDatabase';
import { saveCharacter } from './characterDatabase';
import { SCHEMA_VERSION, downloadJsonFile } from './characterExport';

// ============================================================================
// Types
// ============================================================================

/**
 * Catalog export format containing multiple characters.
 */
export interface CatalogExportData {
  /** Catalog format version */
  catalogVersion: string;
  /** ISO 8601 timestamp when exported */
  exportedAt: string;
  /** Number of characters in the export */
  characterCount: number;
  /** Array of character export data */
  characters: CharacterExportData[];
}

/**
 * Result of importing characters.
 */
export interface ImportResult {
  success: boolean;
  importedCount: number;
  skippedCount: number;
  errors: string[];
  importedCharacters: StoredCharacter[];
}

// ============================================================================
// Constants
// ============================================================================

export const CATALOG_VERSION = '1.0';

// ============================================================================
// Export Functions
// ============================================================================

/**
 * Convert stored characters to catalog export format.
 */
export function createCatalogExport(characters: StoredCharacter[]): CatalogExportData {
  const characterExports: CharacterExportData[] = characters.map((char) => ({
    schemaVersion: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    draft: char.draft,
    ...(char.runtimeState && { runtimeState: char.runtimeState }),
  }));

  return {
    catalogVersion: CATALOG_VERSION,
    exportedAt: new Date().toISOString(),
    characterCount: characters.length,
    characters: characterExports,
  };
}

/**
 * Generate a filename for the catalog export.
 */
export function generateCatalogFilename(): string {
  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  return `character-library-${date}.json`;
}

/**
 * Export the entire catalog to a JSON file and trigger download.
 */
export function downloadCatalogJson(characters: StoredCharacter[]): void {
  const exportData = createCatalogExport(characters);
  const json = JSON.stringify(exportData, null, 2);
  downloadJsonFile(json, generateCatalogFilename());
}

// ============================================================================
// Import Functions
// ============================================================================

/**
 * Type guard to check if data is a catalog export.
 */
export function isCatalogExport(data: unknown): data is CatalogExportData {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;

  return (
    typeof obj.catalogVersion === 'string' &&
    typeof obj.exportedAt === 'string' &&
    typeof obj.characterCount === 'number' &&
    Array.isArray(obj.characters)
  );
}

/**
 * Type guard to check if data is a single character export.
 */
export function isSingleCharacterExport(data: unknown): data is CharacterExportData {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;

  return (
    typeof obj.schemaVersion === 'string' &&
    typeof obj.exportedAt === 'string' &&
    obj.draft !== undefined &&
    typeof obj.draft === 'object'
  );
}

/**
 * Validate a character draft has the minimum required fields.
 */
function validateDraft(draft: unknown): draft is CharacterDraft {
  if (!draft || typeof draft !== 'object') return false;
  const obj = draft as Record<string, unknown>;

  // Minimum required: rulesetId
  return typeof obj.rulesetId === 'string' && obj.rulesetId.length > 0;
}

/**
 * Generate a unique name if there's a conflict.
 */
function makeUniqueName(name: string, existingNames: Set<string>): string {
  if (!existingNames.has(name)) {
    return name;
  }

  // Try adding " (Imported)" suffix
  let candidate = `${name} (Imported)`;
  let counter = 2;

  while (existingNames.has(candidate)) {
    candidate = `${name} (Imported ${counter})`;
    counter++;
  }

  return candidate;
}

/**
 * Import characters from a JSON file.
 * Always additive - never replaces existing characters.
 * Handles both single character exports and catalog exports.
 */
export async function importCharacters(
  jsonData: unknown,
  existingCharacters: StoredCharacter[]
): Promise<ImportResult> {
  const result: ImportResult = {
    success: true,
    importedCount: 0,
    skippedCount: 0,
    errors: [],
    importedCharacters: [],
  };

  // Build set of existing names for conflict detection
  const existingNames = new Set(existingCharacters.map((c) => c.name));

  // Determine if this is a catalog or single character
  let charactersToImport: CharacterExportData[] = [];

  if (isCatalogExport(jsonData)) {
    charactersToImport = jsonData.characters;
  } else if (isSingleCharacterExport(jsonData)) {
    charactersToImport = [jsonData];
  } else {
    result.success = false;
    result.errors.push('Invalid file format. Expected a character or library JSON file.');
    return result;
  }

  // Import each character
  for (const charExport of charactersToImport) {
    try {
      // Validate the draft
      if (!validateDraft(charExport.draft)) {
        result.skippedCount++;
        result.errors.push(`Skipped invalid character: missing required fields.`);
        continue;
      }

      const draft = charExport.draft as CharacterDraft;

      // Generate new UUID to avoid conflicts
      const newId = crypto.randomUUID();

      // Handle name conflicts
      const originalName = draft.name || 'Unnamed Character';
      const uniqueName = makeUniqueName(originalName, existingNames);

      // Update draft with new ID and name
      const importedDraft: CharacterDraft = {
        ...draft,
        id: newId,
        name: uniqueName,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Save to database
      const saveResult = await saveCharacter(
        importedDraft,
        charExport.runtimeState as RuntimeState | undefined
      );

      if (saveResult.success && saveResult.data) {
        result.importedCount++;
        result.importedCharacters.push(saveResult.data);
        existingNames.add(uniqueName); // Track for subsequent imports
      } else {
        result.skippedCount++;
        result.errors.push(`Failed to import "${originalName}": ${saveResult.errors.join(', ')}`);
      }
    } catch (error) {
      result.skippedCount++;
      const message = error instanceof Error ? error.message : 'Unknown error';
      result.errors.push(`Failed to import character: ${message}`);
    }
  }

  // Mark as failed if no characters were imported
  if (result.importedCount === 0 && charactersToImport.length > 0) {
    result.success = false;
  }

  return result;
}

/**
 * Read and parse a JSON file from a File object.
 */
export function readJsonFile(file: File): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result as string);
        resolve(data);
      } catch {
        reject(new Error('Invalid JSON file. Please check the file format.'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file.'));
    };

    reader.readAsText(file);
  });
}
